/**
 * ExpensesHistory Component
 *
 * A visually appealing, user-friendly expenses history display
 * that shows subscriptions, purchases, fees, and service charges.
 *
 * Features:
 * - Real-time expenses updates (future)
 * - Responsive design (mobile-first)
 * - Pagination with configurable page sizes
 * - Type-coded and status-coded badges
 * - Color-coded amount indicators (red for expenses)
 * - Smooth animations for new expenses
 */

import React, { useState, useMemo, useCallback, useLayoutEffect, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import type { Expense } from '../../types/expenses';
import { useAuth } from '../../context/AuthContext';
import { useMaybeWalletExpenses } from '../../context/WalletExpensesContext';
import { useMaybeOLVRate } from '../../context/OLVRateContext';
import { CopyableAmount } from '../common/CopyableAmount';
import { CopyableId, CopyableMember, CopyableSystem, CopyableStatus, InlineCopyableText, CopyableTags } from '../common/CopyableValue';
import { getCurrencyLabel } from '../common/CopyableAmount/utils';
import { usdToWu, formatWu, formatWuDetail, WU_LABEL } from '../../config/workUnits';
import {
  type SummaryPeriod,
  filterByPeriod,
  findSmallestPeriodWithData,
  getPeriodDataAvailability
} from '../../utils/timePeriodHelpers';
import {
  detectProviderFromModelId,
  getPublicSystemNameForUsage,
  getProviderColor,
  resolveEffortCodeWordForDisplay,
} from '../../config/providerCodeNames';
import { friendlySummary } from '../../config/usageVocabulary';
import { foldAuxExpenses } from './foldAuxExpenses';

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-20px);
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: visible;

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }

  &:hover {
    box-shadow: 0 0.75rem 2.5rem rgba(0, 0, 0, 0.1);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.25rem;
  color: #1a1a2e;
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TitleIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  border-radius: 0.5rem;
  color: white;
  font-size: 1rem;

  @media (min-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
    font-size: 1.125rem;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--brand-gold);
  text-transform: lowercase;
`;

const Select = styled.select`
  padding: 0.375rem 0.75rem;
  border: 1px solid #E8DFC4;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.85rem;
  color: #A07D3A;
  cursor: pointer;
  text-transform: lowercase;
  transition: all 0.2s;

  &:hover {
    border-color: var(--brand-gold);
  }

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.1);
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: 1px solid ${props => props.disabled ? '#E8DFC4' : 'var(--brand-gold)'};
  border-radius: 0.5rem;
  background: ${props => props.disabled ? '#FDFBF5' : 'white'};
  color: ${props => props.disabled ? 'var(--brand-gold)' : 'var(--brand-gold)'};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-transform: lowercase;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--brand-gold);
    color: white;
  }
`;

const PageInfo = styled.span`
  font-size: 0.85rem;
  color: var(--brand-gold);
  padding: 0 0.5rem;
  text-transform: lowercase;
`;

const TableWrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #E8DFC4;
  background: white;
  position: relative;
  overflow: visible;
`;

const TableScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 0.75rem;
  padding-top: 0.25rem;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #F5F0E1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #DDD4B8;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--brand-gold);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  overflow: visible;

  /* Mobile: Show only critical columns (date, summary, amount) */
  @media (max-width: 479px) {
    min-width: 100%;

    [data-column="tags"],
    [data-column="id"],
    [data-column="member"],
    [data-column="system"],
    [data-column="status"] {
      display: none;
    }
  }

  /* Small mobile: Add tags and status (5 columns) */
  @media (min-width: 480px) and (max-width: 767px) {
    min-width: 100%;

    [data-column="id"],
    [data-column="member"],
    [data-column="system"] {
      display: none;
    }
  }

  /* Tablet: Show 6 columns (hide id, member) */
  @media (min-width: 768px) and (max-width: 1023px) {
    min-width: 680px;

    [data-column="id"],
    [data-column="member"] {
      display: none;
    }
  }

  /* Desktop: Show all 8 columns */
  @media (min-width: 1024px) {
    min-width: 840px;
  }

  thead, tbody {
    overflow: visible;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.25rem 0.75rem;
  border-bottom: 2px solid #E8DFC4;
  color: var(--brand-gold-hover);
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: lowercase;
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;

  /* Center icon columns */
  &:nth-child(2),
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6),
  &:nth-child(7),
  &:nth-child(8) {
    text-align: center;
  }

  /* MOBILE (< 480px): 3 columns - date (20%), summary (55%), amount (25%) */
  @media (max-width: 479px) {
    &[data-column="date"] { width: 20%; }
    &[data-column="summary"] { width: 55%; }
    &[data-column="amount"] { width: 25%; }
  }

  /* SMALL MOBILE (480-767px): 5 columns - date (15%), tags (10%), summary (45%), amount (20%), status (10%) */
  @media (min-width: 480px) and (max-width: 767px) {
    &[data-column="date"] { width: 15%; }
    &[data-column="tags"] { width: 10%; }
    &[data-column="summary"] { width: 45%; }
    &[data-column="amount"] { width: 20%; }
    &[data-column="status"] { width: 10%; }
  }

  /* TABLET (768-1023px): 6 columns */
  @media (min-width: 768px) and (max-width: 1023px) {
    padding: 0.3rem 0.875rem;
    font-size: 0.75rem;

    &[data-column="date"] { width: 13%; }
    &[data-column="tags"] { width: 8%; }
    &[data-column="summary"] { width: 42%; }
    &[data-column="amount"] { width: 15%; }
    &[data-column="system"] { width: 12%; }
    &[data-column="status"] { width: 10%; }
  }

  /* DESKTOP (1024px+): All 8 columns */
  @media (min-width: 1024px) {
    padding: 0.35rem 1rem;
    font-size: 0.8rem;

    &[data-column="date"] { width: 11%; }
    &[data-column="tags"] { width: 7%; }
    &[data-column="summary"] { width: 32%; }
    &[data-column="id"] { width: 10%; }
    &[data-column="member"] {
      width: 10%;
      padding-left: 0.9rem;
    }
    &[data-column="amount"] { width: 10%; }
    &[data-column="system"] { width: 10%; }
    &[data-column="status"] { width: 10%; }
  }

  @media (min-width: 1200px) {
    padding: 0.4rem 1.25rem;
    font-size: 0.85rem;
  }
`;

/* R18: per-model-call breakdown row under an expanded expense — palette-
   consistent with the wallet's gold/cream scheme, mirroring the chat ⓘ
   popup's "N model calls · in call order" series. */
const BreakdownCell = styled.td`
  padding: 0.75rem 1rem;
  background: #FBF8F0;
  border-bottom: 1px solid #E8DFC4;
`;

const BreakdownTitle = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #A07D3A;
  text-transform: lowercase;
  margin-bottom: 0.375rem;
`;

const BreakdownTable = styled.table`
  border-collapse: collapse;
  font-size: 0.75rem;

  th {
    text-align: left;
    font-weight: 500;
    color: #A07D3A;
    text-transform: lowercase;
    font-size: 0.6875rem;
    padding: 0 1rem 0.25rem 0;
  }
  td {
    padding: 0.25rem 1rem 0.25rem 0;
    color: #1a1a2e;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  th.numeric,
  td.numeric {
    text-align: right;
  }
`;

const BreakdownDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  margin-right: 0.375rem;
`;

const Tr = styled.tr<{ $isNew?: boolean; $isRemoving?: boolean }>`
  transition: background-color 0.2s;
  overflow: visible;

  ${props => props.$isNew && css`
    animation: ${fadeIn} 0.5s ease-out;
    background-color: rgba(198, 167, 94, 0.05);
  `}

  ${props => props.$isRemoving && css`
    animation: ${fadeOut} 0.3s ease-out forwards;
    pointer-events: none;
  `}

  &:hover {
    background-color: #FDFBF5;
  }

  &:nth-child(even) {
    background-color: rgba(248, 250, 252, 0.5);
  }

  &:nth-child(even):hover {
    background-color: #F5F0E1;
  }
`;

const Td = styled.td`
  padding: 0.25rem 0.75rem;
  border-bottom: 1px solid #F5F0E1;
  color: #A07D3A;
  font-size: 0.8rem;
  vertical-align: middle;
  overflow: visible;
  position: relative;

  /* Center the icon columns (tags, id, member, amount, system, status) */
  &:nth-child(2),
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6),
  &:nth-child(7),
  &:nth-child(8) {
    text-align: center;
  }

  /* ===========================================
   * TABLET: 768px+
   * =========================================== */
  @media (min-width: 768px) {
    padding: 0.3rem 0.875rem;
    font-size: 0.85rem;
  }

  /* ===========================================
   * LAPTOP: 1024px+
   * =========================================== */
  @media (min-width: 1024px) {
    padding: 0.35rem 1rem;
    font-size: 0.9rem;
  }

  /* ===========================================
   * DESKTOP: 1200px+
   * =========================================== */
  @media (min-width: 1200px) {
    padding: 0.4rem 1.25rem;
    font-size: 0.95rem;
  }
`;

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  @media (min-width: 768px) {
    gap: 0.125rem;
  }
`;

const DateValue = styled.span`
  font-weight: 600;
  font-size: 0.75rem;
  color: #8B7333;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.85rem;
  }
`;

const TimeValue = styled.span`
  font-size: 0.625rem;
  color: var(--brand-gold);
  font-weight: 500;
  letter-spacing: 0.01em;

  @media (min-width: 768px) {
    font-size: 0.675rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.7rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, #F5F0E1 0%, #E8DFC4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--brand-gold);
`;

const EmptyTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.125rem;
  color: var(--brand-gold-hover);
  margin: 0 0 0.5rem 0;
  font-weight: 500;
  text-transform: lowercase;
`;

const EmptyDescription = styled.p`
  font-size: 0.9rem;
  color: var(--brand-gold);
  margin: 0;
  max-width: 300px;
  text-transform: lowercase;
`;

const SummarySection = styled.div`
  background: linear-gradient(135deg, #FDFBF5 0%, #E8DFC4 100%);
  border-radius: 0.75rem;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const PeriodSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(198, 167, 94, 0.1);

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
  }
`;

const PeriodButton = styled.button<{ $active: boolean; $disabled?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$disabled ? '#9ca3af' : props.$active ? 'var(--brand-gold-hover)' : 'var(--brand-gold)'};
  font-size: 0.8rem;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  text-transform: lowercase;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    background: ${props => props.$disabled ? 'transparent' : props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
    color: ${props => props.$disabled ? '#9ca3af' : 'var(--brand-gold-hover)'};
  }
`;

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (min-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const SummaryLabel = styled.span`
  font-family: Arial, Helvetica, sans-serif !important;  /* Windows-safe fonts only */
  font-size: 0.7rem;  /* Increased to prevent Windows rendering issues */
  color: var(--brand-gold);
  font-weight: 500;
  text-transform: none !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const SummaryValue = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #A07D3A;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const LoadingOverlay = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, #F5F0E1 0%, #E8DFC4 50%, #F5F0E1 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    border-radius: 0.5rem;
  }
`;

const AutoLoadIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: linear-gradient(90deg, #F5F0E1 0%, #E8DFC4 50%, #F5F0E1 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  gap: 0.75rem;
`;

const AutoLoadText = styled.div`
  font-size: 0.875rem;
  color: var(--brand-gold);
  font-weight: 500;
`;

const AutoLoadSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #E8DFC4;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AutoLoadComplete = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: #FAF8F0;
  border: 1px solid #E8DFC4;
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  gap: 0.5rem;
  color: #8B7333;
  font-size: 0.875rem;
  font-weight: 500;
`;

const EmptyPlaceholder = styled.div`
  background: linear-gradient(135deg, #FDFBF5 0%, #F5F0E1 100%);
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px dashed #E8DFC4;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  pointer-events: none;
  cursor: default;

  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }
`;

const EmptyPlaceholderIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, #E8DFC4 0%, #DDD4B8 100%);
  border-radius: 0.625rem;
  color: var(--brand-gold);
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
  }
`;

const EmptyPlaceholderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const EmptyPlaceholderTitle = styled.span`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--brand-gold-hover);
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const EmptyPlaceholderDescription = styled.span`
  font-size: 0.8rem;
  color: var(--brand-gold);
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
`;

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(timestamp: number): { date: string; time: string } {
  const d = new Date(timestamp);
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
}

function formatAmount(amount: number): string {
  if (amount === 0) return '0.00';
  // Very small amounts: show up to 8 decimal places
  if (amount < 0.01) {
    return amount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Small amounts: show up to 6 decimal places
  if (amount < 1) {
    return amount.toFixed(6).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Medium amounts: show up to 4 decimal places
  if (amount < 100) {
    return amount.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Large amounts: show up to 4 decimal places with locale formatting
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Best-available USD for a ledger row (2026-08 PRO Work Units migration —
 * display only): prefer the stored USD snapshot, else reconstruct from the
 * OLV snapshot at the row's historical rate, else fall back to the current
 * live rate. WU = rowUsd / 2.18 (Standard Rate) everywhere it renders.
 */
function deriveRowUsd(
  row: { amountUsd: number; amountOlv: number; olvRateAtTime?: number },
  currentRate: number
): number {
  if (row.amountUsd > 0) return row.amountUsd;
  if (row.amountOlv > 0 && (row.olvRateAtTime ?? 0) > 0) {
    return row.amountOlv * (row.olvRateAtTime as number);
  }
  return row.amountOlv > 0 && currentRate > 0 ? row.amountOlv * currentRate : row.amountUsd;
}

// ============================================================================
// Component
// ============================================================================

interface ExpensesHistoryProps {
  /** Maximum expenses to show (default: unlimited, uses pagination) */
  maxExpenses?: number;
  /** Show the summary bar (default: true) */
  showSummary?: boolean;
  /** Compact mode for embedding in smaller spaces */
  compact?: boolean;
  /** Additional className */
  className?: string;
}

export const ExpensesHistory: React.FC<ExpensesHistoryProps> = ({
  maxExpenses,
  showSummary = true,
  compact = false,
  className,
}) => {
  // Get expenses from Firestore context (with real-time sync)
  const walletExpenses = useMaybeWalletExpenses();
  const isLoading = walletExpenses?.state.isLoading ?? true;
  const expenses = useMemo(() => {
    // Use context data if available, already sorted by timestamp DESC.
    // R19: one row per TASK — background charges (memory update, follow-up
    // suggestions) fold into their parent task row, matching the activity
    // page exactly, so the ledger explains the balance without splitting one
    // task across rows. Ledger docs are untouched (they carry billing
    // idempotency); this is display-time only.
    return foldAuxExpenses(walletExpenses?.state.expenses ?? []);
  }, [walletExpenses?.state.expenses]);

  // Get current user for member lookup
  const { user } = useAuth();

  // Live rate — LAST-RESORT fallback only for legacy rows missing both the
  // USD snapshot and the historical rate (see deriveRowUsd).
  const olvRate = useMaybeOLVRate();
  const currentRate = olvRate?.getEffectiveRate() ?? 0;

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [summaryPeriod, setSummaryPeriod] = useState<SummaryPeriod>('day');
  const [initialPeriodSet, setInitialPeriodSet] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  // R18: which expense row has its per-model-call breakdown open.
  const [expandedBreakdownId, setExpandedBreakdownId] = useState<string | null>(null);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [autoLoadProgress, setAutoLoadProgress] = useState<{ loaded: number; hasMore: boolean } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousScrollTop = useRef<number>(0);

  // Auto-detect best period on mount when data loads
  useEffect(() => {
    if (!initialPeriodSet && !isLoading && expenses.length > 0) {
      const bestPeriod = findSmallestPeriodWithData(expenses);
      setSummaryPeriod(bestPeriod);
      setInitialPeriodSet(true);
    }
  }, [expenses, isLoading, initialPeriodSet]);

  // Auto-load all records when "all" period is selected
  useEffect(() => {
    // Only auto-load when "all" period is selected
    if (summaryPeriod !== 'all') {
      // Clear auto-load state when switching away from "all"
      setIsAutoLoading(false);
      setAutoLoadProgress(null);
      return;
    }

    // Only if there are more records to load
    if (!walletExpenses?.state.hasMore) {
      // No more records - mark as complete
      if (isAutoLoading) {
        setIsAutoLoading(false);
        setAutoLoadProgress(null);
      }
      return;
    }

    // Prevent infinite loops - only load if not currently loading
    if (walletExpenses?.state.isLoading) return;

    // Mark as auto-loading
    if (!isAutoLoading) {
      setIsAutoLoading(true);
    }

    // Update progress
    setAutoLoadProgress({
      loaded: expenses.length,
      hasMore: walletExpenses.state.hasMore,
    });

    // Auto-load more records
    const autoLoad = async () => {
      try {
        await walletExpenses.loadMore();
      } catch (error) {
        console.error('[ExpensesHistory] Auto-load failed:', error);
        setIsAutoLoading(false);
        setAutoLoadProgress(null);
      }
    };

    autoLoad();
  }, [
    summaryPeriod,
    walletExpenses?.state.hasMore,
    walletExpenses?.state.isLoading,
    walletExpenses,
    expenses.length,
    isAutoLoading
  ]);

  // Filter expenses by time period FIRST
  const filteredExpenses = useMemo(() => {
    const baseExpenses = maxExpenses ? expenses.slice(0, maxExpenses) : expenses;
    return filterByPeriod(baseExpenses, summaryPeriod);
  }, [expenses, maxExpenses, summaryPeriod]);

  // Calculate which periods have data (for disabling empty period buttons)
  const periodAvailability = useMemo(() => {
    return getPeriodDataAvailability(expenses);
  }, [expenses]);

  // Calculate paginated data from FILTERED expenses
  const totalExpenses = filteredExpenses.length;
  const totalPages = Math.max(1, Math.ceil(totalExpenses / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalExpenses);
  const pageExpenses = filteredExpenses.slice(startIndex, endIndex);

  // Calculate summary from ALL filtered items
  const summary = useMemo(() => {
    const totalUsd = filteredExpenses.reduce((sum, e) => sum + e.amountUsd, 0);
    // WU total: derive each row's best-available USD, then convert at the
    // Standard Rate — matches the per-row WU figures below.
    const totalWu = filteredExpenses.reduce(
      (sum, e) => sum + usdToWu(deriveRowUsd(e, currentRate)),
      0
    );
    return {
      totalExpenses: filteredExpenses.length,
      totalUsd,
      totalWu,
    };
  }, [filteredExpenses, currentRate]);

  // Reset to page 1 when page size changes
  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setCurrentPage(1);
  }, []);

  // Reset to page 1 when period filter changes
  const handlePeriodChange = useCallback((period: SummaryPeriod) => {
    // Don't allow selecting periods with no data
    if (!getPeriodDataAvailability(expenses)[period]) {
      return;
    }
    setSummaryPeriod(period);
    setCurrentPage(1); // CRITICAL: Reset to page 1 to prevent empty pages
  }, [expenses]);

  // Preserve scroll position during real-time updates
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Save scroll position before render
    previousScrollTop.current = container.scrollTop;

    // Restore after render (if content didn't dramatically change)
    if (container.scrollTop === 0 && previousScrollTop.current > 0) {
      container.scrollTop = previousScrollTop.current;
    }
  }, [walletExpenses?.state.version]);

  if (isLoading) {
    return (
      <Container className={className}>
        <Header>
          <Title>
            <TitleIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              </svg>
            </TitleIcon>
            expenses
          </Title>
        </Header>
        <LoadingOverlay />
      </Container>
    );
  }

  // Check if there's ANY unfiltered data
  const hasAnyData = expenses.length > 0;

  // No data at all
  if (!hasAnyData) {
    return (
      <EmptyPlaceholder className={className}>
        <EmptyPlaceholderIcon>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3l-5 3Z" />
            <path d="M12 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3l-5 3Z" />
            <path d="M7 14c3.22-2.91 4.29-8.75 5-12 1.66 2.38 4.94 9 5 12" />
            <path d="M22 9c-4.29 0-7.14-2.33-10-7 5.71 0 10 4.67 10 7Z" />
          </svg>
        </EmptyPlaceholderIcon>
        <EmptyPlaceholderDescription>any expenses that accrue will be organized here</EmptyPlaceholderDescription>
      </EmptyPlaceholder>
    );
  }

  // Has data but filtered out
  if (totalExpenses === 0 && hasAnyData) {
    return (
      <EmptyPlaceholder className={className}>
        <EmptyPlaceholderIcon>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3l-5 3Z" />
            <path d="M12 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3l-5 3Z" />
            <path d="M7 14c3.22-2.91 4.29-8.75 5-12 1.66 2.38 4.94 9 5 12" />
            <path d="M22 9c-4.29 0-7.14-2.33-10-7 5.71 0 10 4.67 10 7Z" />
          </svg>
        </EmptyPlaceholderIcon>
        <EmptyPlaceholderDescription>
          no expenses in this time period. try selecting "all" to see all data.
        </EmptyPlaceholderDescription>
      </EmptyPlaceholder>
    );
  }

  return (
    <Container className={className}>
      <Header>
        <Title>
          <TitleIcon>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M2 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3l-5 3Z" />
              <path d="M12 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3l-5 3Z" />
              <path d="M7 14c3.22-2.91 4.29-8.75 5-12 1.66 2.38 4.94 9 5 12" />
              <path d="M22 9c-4.29 0-7.14-2.33-10-7 5.71 0 10 4.67 10 7Z" />
            </svg>
          </TitleIcon>
          expenses
        </Title>

        {!compact && totalExpenses > 0 && (
          <Controls>
            <ControlGroup>
              <span>show</span>
              <Select value={pageSize} onChange={handlePageSizeChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
            </ControlGroup>

            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  prev
                </PageButton>
                <PageInfo>
                  {currentPage} / {totalPages}
                </PageInfo>
                <PageButton
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  next
                </PageButton>
              </Pagination>
            )}
          </Controls>
        )}
      </Header>

      {showSummary && totalExpenses > 0 && (
        <SummarySection>
          <PeriodSelector>
            <PeriodButton
              $active={summaryPeriod === 'day'}
              $disabled={!periodAvailability.day}
              onClick={() => handlePeriodChange('day')}
              title={!periodAvailability.day ? 'No data in this period' : undefined}
            >
              24h
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'week'}
              $disabled={!periodAvailability.week}
              onClick={() => handlePeriodChange('week')}
              title={!periodAvailability.week ? 'No data in this period' : undefined}
            >
              7d
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'month'}
              $disabled={!periodAvailability.month}
              onClick={() => handlePeriodChange('month')}
              title={!periodAvailability.month ? 'No data in this period' : undefined}
            >
              30d
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'all'}
              $disabled={!periodAvailability.all}
              onClick={() => handlePeriodChange('all')}
              title={!periodAvailability.all ? 'No data available' : undefined}
            >
              all
            </PeriodButton>
          </PeriodSelector>
          <SummaryBar>
            <SummaryItem>
              <SummaryLabel>expenses</SummaryLabel>
              <SummaryValue>{summary.totalExpenses.toLocaleString()}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>total {getCurrencyLabel('usd')}</SummaryLabel>
              <SummaryValue>-${formatAmount(summary.totalUsd)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>total {getCurrencyLabel('wu')}</SummaryLabel>
              <SummaryValue>-{formatWu(summary.totalWu)}</SummaryValue>
            </SummaryItem>
          </SummaryBar>
        </SummarySection>
      )}

      {/* Show loading indicator and hide table during auto-load */}
      {isAutoLoading ? (
        <AutoLoadIndicator>
          <AutoLoadSpinner />
          <AutoLoadText>
            Loading expenses... ({autoLoadProgress?.loaded ?? 0}{autoLoadProgress?.hasMore ? '+' : ''} loaded)
          </AutoLoadText>
        </AutoLoadIndicator>
      ) : (
      <TableWrapper>
        <TableScrollContainer ref={scrollContainerRef}>
          <Table>
            <thead>
              <tr>
                <Th data-column="date">date / time</Th>
                <Th data-column="tags">tags</Th>
                <Th data-column="summary">summary</Th>
                <Th data-column="id">id</Th>
                <Th data-column="member">member</Th>
                <Th data-column="amount">amount</Th>
                <Th data-column="system">system</Th>
                <Th data-column="status">status</Th>
              </tr>
            </thead>
            <tbody>
              {pageExpenses.map((expense) => {
                const { date, time } = formatDate(expense.timestamp);
                const gens = expense.generations || [];
                const isExpanded = expandedBreakdownId === expense.id;
                // Display-only WU conversion at the Standard Rate ($2.18/WU).
                const rowUsd = deriveRowUsd(expense, currentRate);
                const rowWu = usdToWu(rowUsd);

                return (
                  <React.Fragment key={expense.id}>
                  <Tr
                    $isRemoving={removingIds.has(expense.id)}
                    onClick={gens.length > 0 ? () => setExpandedBreakdownId(isExpanded ? null : expense.id) : undefined}
                    style={gens.length > 0 ? { cursor: 'pointer' } : undefined}
                    aria-expanded={gens.length > 0 ? isExpanded : undefined}
                  >
                    <Td data-column="date">
                      <DateCell>
                        <DateValue>{date}</DateValue>
                        <TimeValue>{time}</TimeValue>
                      </DateCell>
                    </Td>
                    <Td data-column="tags">
                      <CopyableTags tags={expense.tags || []} />
                    </Td>
                    <Td data-column="summary">
                      {/* R19: task rows carry the task's own words (server
                          writes the concise request snippet); legacy system
                          copy normalizes to the task/step vocabulary. */}
                      <InlineCopyableText
                        text={friendlySummary(expense.summary) || expense.summary}
                        emptyText="no summary"
                      />
                    </Td>
                    <Td data-column="id">
                      <CopyableId
                        id={expense.id}
                        type="transaction"
                      />
                    </Td>
                    <Td data-column="member">
                      <CopyableMember
                        memberId={expense.memberId || user?.profileId || user?.id}
                        fullName={user
                          ? (user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`.trim()
                              : user.name)
                          : undefined}
                        email={user?.email}
                      />
                    </Td>
                    <Td data-column="amount">
                      <CopyableAmount
                        olvValue={rowWu}
                        usdValue={rowUsd}
                        unitCurrency="wu"
                        showPositive={false}
                      />
                    </Td>
                    <Td data-column="system">
                      <CopyableSystem
                        modelId={expense.modelId}
                        provider={expense.provider}
                        generations={expense.generations}
                      />
                    </Td>
                    <Td data-column="status">
                      <CopyableStatus status={expense.status === 'completed' ? 'successful' : expense.status === 'pending' ? 'paused' : 'failed'} />
                    </Td>
                  </Tr>
                  {/* R18 (owner directive 2026-08-09): each transaction breaks
                      out its individual system calls — the SAME per-call series
                      the chat ⓘ popup and the Activity page show, so the three
                      surfaces reconcile line by line. 2026-08-13 PRO Work Units
                      migration: NO raw token counts render here (tokens are
                      internal-only) — each call shows its public station name
                      plus its WU (Standard Rate $2.18/WU) and USD only. */}
                  {isExpanded && gens.length > 0 && (
                    <tr>
                      <BreakdownCell colSpan={8}>
                        <BreakdownTitle>
                          {gens.length} system call{gens.length === 1 ? '' : 's'} · in call order
                        </BreakdownTitle>
                        <BreakdownTable>
                          <thead>
                            <tr>
                              <th>call</th>
                              <th>system</th>
                              <th className="numeric">{WU_LABEL.toLowerCase()}</th>
                              <th className="numeric">usd</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gens.map((g, gi) => {
                              const genUsd = g.userCostUsd || 0;
                              return (
                                <tr key={`${expense.id}_g${gi}`}>
                                  <td>{gi + 1}</td>
                                  <td>
                                    <BreakdownDot
                                      $color={getProviderColor(detectProviderFromModelId(g.model))}
                                    />
                                    {getPublicSystemNameForUsage(g.model, expense.provider)}
                                    {/* R20 (Miles: effort always shows unless
                                        none): stored effort wins, else the
                                        step's model resolves it from the SSOT
                                        — lockstep with the ⓘ popup and the
                                        activity page. */}
                                    {resolveEffortCodeWordForDisplay(g.model, g.effort) && (
                                      <span style={{ opacity: 0.55 }}>{` · ${resolveEffortCodeWordForDisplay(g.model, g.effort)}`}</span>
                                    )}
                                  </td>
                                  <td className="numeric">{formatWuDetail(usdToWu(genUsd))}</td>
                                  <td className="numeric">{`$${genUsd.toFixed(4)}`}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </BreakdownTable>
                      </BreakdownCell>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </TableScrollContainer>
      </TableWrapper>
      )}
    </Container>
  );
};

export default ExpensesHistory;
